import { type ApplicationErrorDto } from '../../services/admin.api';
import { icon } from '../../icons';
import { t } from '../../services/i18n';
import { esc, escAttr, formatDate } from './admin-shared';

const STATUS_OPTIONS = ['', '400', '401', '403', '404', '409', '422', '500'];
const METHOD_OPTIONS = ['', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

function renderStatusOptions(selected: string): string {
  return STATUS_OPTIONS.map((value) => {
    const label = value ? value : t('admin.filter.all');
    return `<option value="${value}" ${selected === value ? 'selected' : ''}>${label}</option>`;
  }).join('');
}

function renderMethodOptions(selected: string): string {
  return METHOD_OPTIONS.map((value) => {
    const label = value ? value : t('admin.filter.all');
    return `<option value="${value}" ${selected === value ? 'selected' : ''}>${label}</option>`;
  }).join('');
}

function renderErrorRow(applicationError: ApplicationErrorDto, index: number, deleting: boolean): string {
  return `
    <tr>
      <td class="admin-cell-date">${formatDate(applicationError.occurredAtUtc)}</td>
      <td class="admin-center"><span class="badge badge-admin-error">${esc(String(applicationError.statusCode))}</span></td>
      <td class="admin-cell-method">${esc(applicationError.method || '-')}</td>
      <td class="admin-cell-path">${esc(applicationError.path || '-')}</td>
      <td class="admin-cell-name">${esc(applicationError.functionName)}</td>
      <td class="admin-cell-name">${esc(applicationError.exceptionType)}</td>
      <td class="admin-cell-message">${esc(applicationError.message)}</td>
      <td class="admin-center">
        <button class="admin-eye-btn" data-index="${index}" title="${t('admin.errors.viewDetails')}">
          ${icon('eye', { size: 15 })}
        </button>
        <button class="admin-eye-btn admin-eye-btn--danger" data-delete-index="${index}" title="${t('admin.errors.delete')}" ${deleting ? 'disabled' : ''}>
          ${icon('trash', { size: 15 })}
        </button>
      </td>
    </tr>
  `;
}

export class RcAdminErrorsView extends HTMLElement {
  applicationErrors: ApplicationErrorDto[] = [];
  totalCount = 0;
  page = 1;
  pageSize = 50;
  totalPages = 0;
  deletingIds = new Set<string>();
  filters = {
    functionName: '',
    path: '',
    method: '',
    userId: '',
    search: '',
    statusCode: '',
    occurredFrom: '',
    occurredTo: '',
  };

  render(): void {
    this.innerHTML = `
      <div class="card">
        <form class="admin-users-filters">
          <div class="form-row">
            <div class="form-group">
              <label for="admin-error-filter-function">${t('admin.errors.filter.functionName')}</label>
              <input id="admin-error-filter-function" name="functionName" type="text" value="${escAttr(this.filters.functionName)}" placeholder="${t('admin.errors.filter.functionNamePlaceholder')}" />
            </div>
            <div class="form-group">
              <label for="admin-error-filter-path">${t('admin.errors.filter.path')}</label>
              <input id="admin-error-filter-path" name="path" type="text" value="${escAttr(this.filters.path)}" placeholder="${t('admin.errors.filter.pathPlaceholder')}" />
            </div>
            <div class="form-group">
              <label for="admin-error-filter-method">${t('admin.errors.filter.method')}</label>
              <select id="admin-error-filter-method" name="method">${renderMethodOptions(this.filters.method)}</select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="admin-error-filter-user">${t('admin.errors.filter.userId')}</label>
              <input id="admin-error-filter-user" name="userId" type="text" value="${escAttr(this.filters.userId)}" placeholder="${t('admin.errors.filter.userIdPlaceholder')}" />
            </div>
            <div class="form-group">
              <label for="admin-error-filter-search">${t('admin.errors.filter.search')}</label>
              <input id="admin-error-filter-search" name="search" type="text" value="${escAttr(this.filters.search)}" placeholder="${t('admin.errors.filter.searchPlaceholder')}" />
            </div>
            <div class="form-group">
              <label for="admin-error-filter-status">${t('admin.errors.filter.statusCode')}</label>
              <select id="admin-error-filter-status" name="statusCode">${renderStatusOptions(this.filters.statusCode)}</select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="admin-error-filter-from">${t('admin.errors.filter.occurredFrom')}</label>
              <input id="admin-error-filter-from" name="occurredFrom" type="date" value="${escAttr(this.filters.occurredFrom)}" />
            </div>
            <div class="form-group">
              <label for="admin-error-filter-to">${t('admin.errors.filter.occurredTo')}</label>
              <input id="admin-error-filter-to" name="occurredTo" type="date" value="${escAttr(this.filters.occurredTo)}" />
            </div>
            <div class="admin-users-filter-actions">
              <button type="submit" class="btn btn-primary">${t('admin.filter.apply')}</button>
              <button type="button" class="btn btn-outline" data-action="reset-filters">${t('admin.filter.reset')}</button>
            </div>
          </div>
        </form>
        <div class="admin-users-toolbar">
          <div class="admin-users-meta">${t('admin.errors.count', { count: this.totalCount })}</div>
          <div class="admin-pagination-info">${this.renderPaginationSummary()}</div>
        </div>
        <div class="table-wrapper">
          <table class="admin-table admin-errors-table">
            <thead>
              <tr>
                <th class="admin-col-date">${t('admin.errors.col.occurredAt')}</th>
                <th class="admin-col-status">${t('admin.errors.col.statusCode')}</th>
                <th class="admin-col-method">${t('admin.errors.col.method')}</th>
                <th class="admin-col-path">${t('admin.errors.col.path')}</th>
                <th class="admin-col-name">${t('admin.errors.col.functionName')}</th>
                <th class="admin-col-name">${t('admin.errors.col.exceptionType')}</th>
                <th class="admin-col-message">${t('admin.errors.col.message')}</th>
                <th class="admin-col-action"></th>
              </tr>
            </thead>
            <tbody>
              ${this.applicationErrors.length
                ? this.applicationErrors.map((applicationError, index) => renderErrorRow(applicationError, index, this.deletingIds.has(applicationError.id))).join('')
                : `<tr><td colspan="8" class="empty-state admin-users-empty">${t('admin.errors.noResults')}</td></tr>`}
            </tbody>
          </table>
        </div>
        <div class="admin-pagination">
          <div class="admin-pagination-info">${t('admin.pagination.page', {
            page: this.totalPages === 0 ? 0 : this.page,
            totalPages: this.totalPages,
          })}</div>
          <div class="admin-pagination-actions">
            <button type="button" class="btn btn-outline" data-page="${this.page - 1}" ${this.page <= 1 ? 'disabled' : ''}>${t('admin.pagination.previous')}</button>
            <button type="button" class="btn btn-outline" data-page="${this.page + 1}" ${this.page >= this.totalPages ? 'disabled' : ''}>${t('admin.pagination.next')}</button>
          </div>
        </div>
      </div>
    `;

    this.querySelector<HTMLFormElement>('.admin-users-filters')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.dispatchEvent(new CustomEvent('admin-errors-apply-filters', {
        bubbles: true,
        detail: { form: event.currentTarget as HTMLFormElement },
      }));
    });
    this.querySelector('[data-action="reset-filters"]')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('admin-errors-reset-filters', { bubbles: true }));
    });
    this.querySelectorAll<HTMLButtonElement>('[data-index]').forEach((button) => {
      button.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('admin-errors-open-details', {
          bubbles: true,
          detail: { index: Number(button.dataset.index) },
        }));
      });
    });
    this.querySelectorAll<HTMLButtonElement>('[data-delete-index]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.dispatchEvent(new CustomEvent('admin-errors-delete', {
          bubbles: true,
          detail: { index: Number(button.dataset.deleteIndex) },
        }));
      });
    });
    this.querySelectorAll<HTMLButtonElement>('[data-page]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.dispatchEvent(new CustomEvent('admin-errors-page', {
          bubbles: true,
          detail: { page: Number(button.dataset.page) },
        }));
      });
    });
  }

  private renderPaginationSummary(): string {
    if (this.totalCount === 0) {
      return t('admin.pagination.noResults');
    }
    const start = (this.page - 1) * this.pageSize + 1;
    const end = start + this.applicationErrors.length - 1;
    return t('admin.pagination.summary', { start, end, total: this.totalCount });
  }
}

if (!customElements.get('rc-admin-errors-view')) {
  customElements.define('rc-admin-errors-view', RcAdminErrorsView);
}
